import { useMemo } from 'react';
import sanitizeHtml, { IOptions } from 'sanitize-html';
import { rewriteStaticAssetUrl } from '@/lib/static-asset-url';

const HASH_LIKE_NAME = /^[0-9a-f-]{16,}$/i;
const GENERIC_FILE_NAMES = new Set(['content-inline', 'image', 'img', 'file', 'download']);

/** `.../qualidoc-FP-Mercado+pago.svg` → `Mercado pago`; hash-like names → `''` (decorative). */
export function altFromImageSrc(src?: string): string {
  if (!src) return '';
  let fileName = src.split(/[?#]/)[0].split('/').pop() ?? '';
  try {
    fileName = decodeURIComponent(fileName);
  } catch {
    // Keep the raw name when it is not valid URI encoding.
  }
  const base = fileName
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/^qualidoc[-_]/i, '')
    .replace(/^FP[-_]/, '');
  if (!base || HASH_LIKE_NAME.test(base) || GENERIC_FILE_NAMES.has(base.toLowerCase())) {
    return '';
  }
  return base.replace(/[-_+]+/g, ' ').replace(/\s+/g, ' ').trim();
}

const SANITIZE_OPTIONS: IOptions = {
  transformTags: {
    img: (tagName, attribs) =>
      'alt' in attribs
        ? { tagName, attribs }
        : { tagName, attribs: { ...attribs, alt: altFromImageSrc(attribs.src) } },
  },
  allowedAttributes: {
    '*': ['style', 'class'],
    a: ['href', 'name', 'target', 'style', 'class'],
    h1: ['color', 'style'],
    h2: ['color', 'style'],
    img: [
      'src',
      'srcset',
      'alt',
      'title',
      'width',
      'height',
      'loading',
      'class',
      'style',
    ],
    iframe: [
      'src',
      'frameborder',
      'allowfullscreen',
      'class',
      'style',
      'height',
      'width',
      'border',
      'title',
      'allow',
      'referrerpolicy',
    ],
    input: ['id', 'type', 'value', 'placeholder', 'class', 'style'],
    label: ['id', 'for', 'class', 'style'],
    svg: [
      'xmlns',
      'viewBox',
      'class',
      'style',
      'fill',
      'height',
      'width',
      'viewbox',
      'xmlns',
    ],
    path: ['d', 'fill', 'stroke', 'stroke-width', 'clip-rule'],
  },
  allowedIframeHostnames: ['www.youtube.com', 'youtube.com'],
  allowedStyles: {
    '*': {
      color: [
        /^#(0x)?[0-9a-f]+$/i,
        /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
      ],
      'background-color': [
        /^#(0x)?[0-9a-f]+$/i,
        /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
      ],
      'text-align': [/^left$/, /^right$/, /^center$/],
      'font-size': [/^\d+(?:px|em|%|rem|vw|vh)$/],
    },
  },
  allowedSchemesByTag: {
    img: ['data', 'http', 'https'],
  },
  allowedTags: false,
  allowedSchemes: ['http', 'https', 'data', 'ftp', 'mailto', 'tel'],
  allowProtocolRelative: true,
  allowVulnerableTags: true,
};

export function sanitizeContent(content?: string | null): string {
  if (!content) return '';
  return sanitizeHtml(rewriteStaticAssetUrl(content), SANITIZE_OPTIONS);
}

export function useSanitizeContent({
  content,
}: {
  content?: string | null;
}): string {
  return useMemo(() => sanitizeContent(content), [content]);
}
